/**
 * Perfil (rediseño AI Studio, 15-10; prototipo `PerfilView.tsx`). Datos reales de `profiles` y de la sesión: nombre
 * (editable), correo (bloqueado), rol, fecha de alta y estado de la cuenta. Filas: Alertas por correo (estado real de la
 * suscripción), Contraseña (enlace por el flujo de recuperación que ya existe) y Cerrar sesión. «Administración del
 * estudio» solo para admin. Escondidos por no tener datos: tono por defecto del asistente y sesiones/dispositivo.
 * Decisión del dueño (15-10): sin las preferencias viejas (frecuencia y temas); `profiles.preferences` no se toca.
 */
import { FormEvent, useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Bell, Check, ChevronDown, ChevronRight, Lock, LogOut } from 'lucide-react';
import Layout from '../components/layout/Layout';
import { Columna, FOCO, Relleno } from '../components/v3/Ajustes';
import { supabase } from '../utils/supabase';
import { useAuth } from '../contexts/AuthContext';
import { authService } from '../services/auth.service';
import { notificationsService, SubscriptionState } from '../services/notifications.service';
import { fechaLarga } from '../utils/fechas';

type Cuenta = { name: string | null; role: 'admin' | 'user'; status?: 'pending' | 'approved' | 'rejected' | null; created_at: string };

const ESTADO_CUENTA: Record<string, { texto: string; clase: string }> = {
  approved: { texto: 'Cuenta activa', clase: 'bg-emerald-50 text-emerald-700' },
  pending: { texto: 'Pendiente de aprobación', clase: 'bg-amber-50 text-amber-700' },
  rejected: { texto: 'Cuenta rechazada', clase: 'bg-red-50 text-red-700' },
};

/** «GZ» desde el nombre; si no hay nombre, la primera letra del correo. */
export function iniciales(nombre: string, correo: string): string {
  const partes = nombre.trim().split(/\s+/).filter(Boolean);
  if (partes.length >= 2) return (partes[0][0] + partes[partes.length - 1][0]).toUpperCase();
  if (partes.length === 1) return partes[0].slice(0, 2).toUpperCase();
  return (correo[0] || '?').toUpperCase();
}

/** Texto de la fila de Alertas según la suscripción real (null = cargando, undefined = no se pudo leer). */
function resumenAlertas(estado: SubscriptionState | null | undefined): string {
  if (estado === undefined) return 'Resumen diario de lo nuevo del SII';
  if (!estado) return '';
  const { subscription, delivery } = estado;
  if (!subscription) return 'Sin suscripción · resumen diario de lo nuevo del SII';
  if (!subscription.enabled) return 'Pausado · resumen diario de lo nuevo del SII';
  return delivery?.digest_enabled ? 'Suscrito · lunes a viernes a las 9:00' : 'Suscrito · los envíos aún no están activos';
}

const fila = 'py-4 flex items-center justify-between gap-4';
const tituloFila = 'text-[13.5px] font-bold text-[#142D4E]';
const ayudaFila = 'text-[12px] text-slate-500';
const accionFila = 'text-[12.5px] text-slate-600 group-hover:text-[#142D4E] font-medium flex items-center gap-1 shrink-0';

export default function Perfil() {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const correo = user?.email || '';

  const [cuenta, setCuenta] = useState<Cuenta | null>(null);
  const [errorCarga, setErrorCarga] = useState(false);
  const [nombre, setNombre] = useState('');
  const [guardado, setGuardado] = useState('');
  const [estadoGuardar, setEstadoGuardar] = useState<'quieto' | 'guardando' | 'guardado' | 'error'>('quieto');
  const [alertas, setAlertas] = useState<SubscriptionState | null | undefined>(null);
  const [claveAbierta, setClaveAbierta] = useState(false);
  const [enlace, setEnlace] = useState<'quieto' | 'enviando' | 'enviado' | 'error'>('quieto');

  const userId = user?.id;
  const cargar = useCallback(async () => {
    if (!userId) return;
    setErrorCarga(false);
    const { data, error } = await supabase.from('profiles').select('name, role, status, created_at').eq('id', userId).maybeSingle();
    if (error || !data) {
      setErrorCarga(true);
      return;
    }
    setCuenta(data as Cuenta);
    setNombre(data.name || '');
    setGuardado(data.name || '');
  }, [userId]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  // La fila de Alertas muestra el estado real; si falla, una línea genérica (la pantalla de Alertas tiene el detalle).
  useEffect(() => {
    if (!userId) return;
    let vivo = true;
    notificationsService.getSubscription().then(e => vivo && setAlertas(e), () => vivo && setAlertas(undefined));
    return () => { vivo = false; };
  }, [userId]);

  const guardar = async (e: FormEvent) => {
    e.preventDefault();
    const limpio = nombre.trim();
    if (!userId || !limpio || limpio === guardado) return;
    setEstadoGuardar('guardando');
    // Solo el nombre: las preferencias viejas (frecuencia y temas) ya no se escriben (dueño, 15-10).
    const { error } = await supabase.from('profiles').update({ name: limpio }).eq('id', userId);
    if (error) {
      setEstadoGuardar('error');
      return;
    }
    setNombre(limpio);
    setGuardado(limpio);
    setEstadoGuardar('guardado');
  };

  const enviarEnlace = async () => {
    if (!correo) return;
    setEnlace('enviando');
    const { error } = await authService.resetPassword(correo);
    setEnlace(error ? 'error' : 'enviado');
  };

  const salir = async () => {
    await signOut();
    navigate('/login');
  };

  const cargando = !cuenta && !errorCarga;
  const nombreVisible = guardado || correo;
  const estado = ESTADO_CUENTA[cuenta?.status || 'approved'];
  const alta = cuenta ? fechaLarga(cuenta.created_at).replace(/^\d+ de /, '') : '';
  const cambio = nombre.trim() !== guardado && nombre.trim() !== '';
  const indicador =
    estadoGuardar === 'guardando' ? <span className="text-[12px] text-slate-500">Guardando…</span>
    : estadoGuardar === 'error' ? <span className="text-[12px] text-red-700" role="alert">No se pudo guardar; inténtalo de nuevo</span>
    : estadoGuardar === 'guardado' && !cambio ? <span className="text-[12px] text-emerald-700 font-medium flex items-center gap-1" role="status"><Check className="w-3.5 h-3.5" aria-hidden="true" /> Guardado</span>
    : null;

  return (
    <Layout title="Perfil">
      <Columna ancho={880}>
        <div className="py-3">
          {/* Encabezado: iniciales, nombre, estado de la cuenta y rol */}
          <div className="flex flex-wrap items-center justify-between gap-4 mb-7">
            <div className="flex items-center gap-3.5 min-w-0">
              <div className="w-11 h-11 rounded-full font-bold text-[14px] flex items-center justify-center shrink-0 bg-[#EEF2FF] text-[#4F46E5]" aria-hidden="true">
                {cargando ? '' : iniciales(guardado, correo)}
              </div>
              <div className="min-w-0">
                {cargando ? (
                  <div className="space-y-2 w-48"><Relleno ancho="80%" /><Relleno ancho="55%" /></div>
                ) : (
                  <>
                    <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                      <h1 className="text-[16px] font-bold text-[#142D4E] leading-snug break-words min-w-0">{nombreVisible || 'Mi perfil'}</h1>
                      {cuenta && <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ${estado.clase}`}>{estado.texto}</span>}
                    </div>
                    {cuenta && (
                      <p className="text-[12.5px] text-slate-500 mt-0.5">
                        {cuenta.role === 'admin' ? 'Administrador' : 'Usuario'}{alta && ` · desde ${alta}`}
                      </p>
                    )}
                  </>
                )}
              </div>
            </div>

            {cuenta?.role === 'admin' && (
              <Link to="/admin" className={`text-[12.5px] text-slate-500 hover:text-[#142D4E] font-medium flex items-center gap-1 transition-colors motion-reduce:transition-none rounded ${FOCO}`}>
                <span>Administración del estudio</span>
                <ChevronRight className="w-3.5 h-3.5 text-slate-400" aria-hidden="true" />
              </Link>
            )}
          </div>

          {errorCarga && (
            <p className="text-[13px] text-slate-500 mb-6">
              No se pudieron cargar los datos de tu cuenta.{' '}
              <button type="button" onClick={cargar} className={`font-medium text-[#4F46E5] hover:underline rounded ${FOCO}`}>Reintentar</button>
            </p>
          )}

          {/* Datos de la cuenta: nombre y correo */}
          <form onSubmit={guardar} className="mb-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
              <div>
                <label htmlFor="perfil-nombre" className="block text-[12px] font-semibold text-[#142D4E] mb-1.5">Nombre completo</label>
                <input
                  id="perfil-nombre"
                  type="text"
                  value={nombre}
                  maxLength={120}
                  disabled={!cuenta || estadoGuardar === 'guardando'}
                  onChange={e => { setNombre(e.target.value); if (estadoGuardar !== 'guardando') setEstadoGuardar('quieto'); }}
                  placeholder={cuenta ? 'Escribe tu nombre' : ''}
                  className="w-full px-3.5 py-2.5 text-[13.5px] text-[#142D4E] rounded-xl border border-slate-200/90 bg-white placeholder:text-slate-400 focus:outline-none focus:border-indigo-300 disabled:bg-slate-50/90 transition-colors motion-reduce:transition-none"
                />
              </div>

              <div>
                <label htmlFor="perfil-correo" className="block text-[12px] font-semibold text-[#142D4E] mb-1.5">Correo</label>
                <input
                  id="perfil-correo"
                  type="email"
                  value={correo}
                  disabled
                  className="w-full px-3.5 py-2.5 text-[13.5px] text-slate-500 rounded-xl border border-slate-200/80 bg-slate-50/90 cursor-not-allowed truncate"
                />
                <p className="text-[11.5px] text-slate-500 mt-1.5 leading-relaxed">Correo de inicio de sesión y de las alertas. No se puede cambiar desde aquí.</p>
              </div>
            </div>

            <div className="flex items-center gap-3 mt-4 min-h-[34px]">
              <button
                type="submit"
                disabled={!cambio || estadoGuardar === 'guardando'}
                className={`px-4 py-2 rounded-xl text-white font-semibold text-[12.5px] transition-colors motion-reduce:transition-none shadow-2xs bg-[#4F46E5] hover:bg-indigo-700 disabled:bg-slate-200 disabled:text-slate-500 disabled:shadow-none disabled:cursor-not-allowed ${FOCO}`}
              >
                {estadoGuardar === 'guardando' ? 'Guardando…' : 'Guardar'}
              </button>
              <span aria-live="polite">{indicador}</span>
            </div>
          </form>

          {/* Ajustes con revelado progresivo */}
          <div className="divide-y divide-slate-100 border-t border-b border-slate-100">
            <Link to="/notificaciones" className={`${fila} group rounded ${FOCO}`}>
              <div className="flex items-center gap-2.5 min-w-0">
                <Bell className="w-4 h-4 text-slate-400 shrink-0" aria-hidden="true" />
                <div className="min-w-0">
                  <div className={tituloFila}>Alertas por correo</div>
                  <div className={ayudaFila}>{alertas === null ? <span className="block w-56 pt-1"><Relleno ancho="100%" /></span> : resumenAlertas(alertas)}</div>
                </div>
              </div>
              <span className={accionFila}>
                <span>Administrar</span>
                <ChevronRight className="w-3.5 h-3.5 text-slate-400" aria-hidden="true" />
              </span>
            </Link>

            <div className="py-4">
              <button
                type="button"
                onClick={() => setClaveAbierta(v => !v)}
                aria-expanded={claveAbierta}
                aria-controls="perfil-clave"
                className={`w-full flex items-center justify-between gap-4 text-left group rounded ${FOCO}`}
              >
                <div className="flex items-center gap-2.5 min-w-0">
                  <Lock className="w-4 h-4 text-slate-400 shrink-0" aria-hidden="true" />
                  <div className="min-w-0">
                    <div className={tituloFila}>Contraseña</div>
                    <div className={ayudaFila}>Por correo, con un enlace para cambiarla</div>
                  </div>
                </div>
                <span className={accionFila}>
                  <span>{claveAbierta ? 'Cerrar' : 'Cambiar'}</span>
                  <ChevronDown className={`w-3.5 h-3.5 text-slate-400 transition-transform motion-reduce:transition-none ${claveAbierta ? 'rotate-180' : ''}`} aria-hidden="true" />
                </span>
              </button>

              {claveAbierta && (
                <div id="perfil-clave" className="mt-3 ml-6 p-3.5 rounded-xl bg-slate-50/80 border border-slate-200/70 flex flex-wrap items-center justify-between gap-x-4 gap-y-2 text-[12px]">
                  <span className="text-slate-600 min-w-0 break-words" role={enlace === 'enviado' || enlace === 'error' ? 'status' : undefined}>
                    {enlace === 'enviado' ? <>Enlace enviado a <span className="font-medium text-[#142D4E]">{correo}</span>. Revisa tu correo.</>
                      : enlace === 'error' ? <span className="text-red-700">No se pudo enviar el enlace; inténtalo de nuevo.</span>
                      : <>Recibirás un enlace para cambiarla en <span className="font-medium text-[#142D4E]">{correo}</span></>}
                  </span>
                  {enlace !== 'enviado' && (
                    <button
                      type="button"
                      onClick={enviarEnlace}
                      disabled={enlace === 'enviando' || !correo}
                      className={`font-semibold text-[#4F46E5] hover:underline disabled:text-slate-400 disabled:no-underline disabled:cursor-not-allowed shrink-0 rounded ${FOCO}`}
                    >
                      {enlace === 'enviando' ? 'Enviando…' : 'Enviar enlace de cambio →'}
                    </button>
                  )}
                </div>
              )}
            </div>

            <button type="button" onClick={salir} className={`${fila} w-full text-left group rounded ${FOCO}`}>
              <div className="flex items-center gap-2.5">
                <LogOut className="w-4 h-4 text-slate-400" aria-hidden="true" />
                <div>
                  <div className={tituloFila}>Cerrar sesión</div>
                  <div className={ayudaFila}>En este dispositivo</div>
                </div>
              </div>
              <span className="text-[12.5px] text-slate-600 group-hover:text-rose-600 font-medium shrink-0 transition-colors motion-reduce:transition-none">Salir</span>
            </button>
          </div>
        </div>
      </Columna>
    </Layout>
  );
}
